<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-home"></i
          >{{
            `${$label("baubau", "Operator")}${
              noDataExist ? "" : ": " + operatorName
            }`
          }}
          <a
            @click="createOperator"
            class="btn btn-v-lightgreen-white pull-right"
            v-tooltip="{
              content: 'Neuen Betreiber anlegen',
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </a>
          <select
            v-model="selectedOperatorId"
            class="form-control course-select pull-right"
            style="margin-right: 15px;"
          >
            <option
              v-for="(operator, index) in operators.data"
              :value="
                selectedOperatorId === 'default' && index === 0
                  ? 'default'
                  : operator.id
              "
              v-bind:key="operator.id"
              >{{ operator.name }}</option
            >
          </select>
        </h1>
      </div>
    </div>

    <navigation-tabs :operator-id="selectedOperatorId"></navigation-tabs>

    <div v-show="loading" class="row">
      <div class="col-xs-12">
        <view-loader
          margin-top-pixels="15"
          border-color="primary"
        ></view-loader>
      </div>
    </div>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-show="!loading" v-else>
      <div class="row">
        <div class="col-sm-6">
          <h2>
            <i class="fa fa-fw fa-lg fa-bookmark-o"></i>
            {{ $label("operator.company_club", "Company | Club") }}
          </h2>
          <p></p>
          <div id="operator-company" class="v-form-partial">
            <div v-if="selectedOperator !== null" class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-12">
                    <label class="label">{{
                      $label("operator.name_label", "Name")
                    }}</label>
                    <label class="textarea">
                      <textarea
                        v-model="selectedOperator.name"
                        :class="{ 'text-input-error': hasError('name') }"
                        :placeholder="$label('operator.name_placeholder')"
                        :disabled="updating"
                        id="operator-name"
                        rows="2"
                      >
                      </textarea>
                    </label>
                    <p v-if="hasError('name')" class="text-danger">
                      {{ getError("name") }}
                    </p>
                  </section>
                </div>

                <div class="row">
                  <section class="col col-6">
                    <text-input
                      v-model="selectedOperator.fiscal_details.uid"
                      id="operator-uid"
                      :label="$label('operator.uid_number_label', 'UID')"
                      :placeholder="$label('operator.uid_number_placeholder')"
                      :has-error="hasError('fiscal_details.uid')"
                      :error="getError('fiscal_details.uid')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>

                  <section class="col col-6">
                    <text-input
                      v-model="selectedOperator.fiscal_details.fn"
                      id="operator-fn"
                      :label="$label('operator.fn_number_label', 'FN')"
                      :placeholder="$label('operator.fn_number_placeholder')"
                      :has-error="hasError('fiscal_details.fn')"
                      :error="getError('fiscal_details.fn')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                </div>

                <div class="row">
                  <section class="col col-6">
                    <text-input
                      v-model="selectedOperator.fiscal_details.zvr"
                      id="operator-zvr"
                      :label="$label('operator.zvr_number_label', 'ZVR')"
                      :placeholder="$label('operator.zvr_number_placeholder')"
                      :has-error="hasError('fiscal_details.zvr')"
                      :error="getError('fiscal_details.zvr')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>

                  <section class="col col-6">
                    <text-input
                      v-model="selectedOperator.fiscal_details.dvr"
                      id="operator-dvr"
                      :label="$label('operator.dvr_number_label', 'DVR')"
                      :placeholder="$label('operator.dvr_number_placeholder')"
                      :has-error="hasError('fiscal_details.dvr')"
                      :error="getError('fiscal_details.dvr')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                </div>

                <div class="row">
                  <section class="col col-12">
                    <text-input
                      v-model="selectedOperator.email"
                      id="operator-email"
                      :label="
                        $label('common.email_address_long', 'E-Mail address')
                      "
                      :placeholder="$label('common.email_address_short')"
                      :has-error="hasError('email')"
                      :error="getError('email')"
                      :is-disabled="updating"
                      icon="icon-envelope"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                </div>

                <div class="row">
                  <section class="col col-3">
                    <text-input
                      v-model="selectedOperator.phone_number.prefix"
                      id="operator-phone-number-prefix"
                      :label="$label('common.prefix', 'Prefix')"
                      placeholder="+43"
                      :has-error="hasError('phone_number.prefix')"
                      :error="getError('phone_number.prefix')"
                      :is-disabled="updating"
                      :hide-error-text="true"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>

                  <section class="col col-9">
                    <text-input
                      v-model="selectedOperator.phone_number.number"
                      id="operator-phone-number"
                      :label="$label('common.phone_number', 'Phone number')"
                      placeholder="XXXXXXXX"
                      :has-error="hasError('phone_number.number')"
                      :error="getError('phone_number.number')"
                      :is-disabled="updating"
                      icon="icon-phone"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                </div>

                <div class="row">
                  <section class="col col-3">
                    <text-input
                      v-model="selectedOperator.fax_number.prefix"
                      id="operator-fax-number-prefix"
                      :label="$label('common.prefix', 'Prefix')"
                      placeholder="+43"
                      :has-error="hasError('fax_number.prefix')"
                      :error="getError('fax_number.prefix')"
                      :is-disabled="updating"
                      :hide-error-text="true"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>

                  <section class="col col-9">
                    <text-input
                      v-model="selectedOperator.phone_number.number"
                      id="operator-fax-number"
                      :label="$label('common.fax_number', 'Fax number')"
                      placeholder="XXXXXXXX"
                      :has-error="hasError('fax_number.number')"
                      :error="getError('fax_number.number')"
                      :is-disabled="updating"
                      icon="icon-print"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                </div>

                <div class="row">
                  <section class="col col-12">
                    <text-input
                      v-model="selectedOperator.website"
                      id="operator-website"
                      :label="$label('operator.homepage', 'Homepage')"
                      placeholder="https://google.com"
                      :has-error="hasError('website')"
                      :error="getError('website')"
                      :is-disabled="updating"
                      icon="icon-website"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                </div>
              </fieldset>
            </div>
          </div>
        </div>

        <div class="col-sm-6 tooltip-wrapper">
          <h2>
            <i class="fa fa-fw fa-lg fa-home"></i>
            {{ $label("common.address", "Address") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div v-if="selectedOperator !== null" class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Country -->
                  <section class="col col-6">
                    <label class="label">{{ $label("common.country") }}</label>

                    <label class="select">
                      <select
                        v-model="selectedOperator.address.country_id"
                        :disabled="updating"
                      >
                        <option
                          v-for="country in countries"
                          :value="country.id"
                          :selected="countryShouldBeSelected(country)"
                          v-bind:key="country.id"
                          >{{
                            $label("common.country_" + country.slug)
                          }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p
                      v-if="hasError('address.country_id')"
                      class="text-danger"
                    >
                      {{ getError("address.country_id") }}
                    </p>
                  </section>
                  <!-- END Country -->

                  <!-- BEGIN State -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.state", "State")
                    }}</label>

                    <label class="select">
                      <select
                        v-model="selectedOperator.address.state_id"
                        :disabled="updating"
                      >
                        <option
                          v-for="state in statesOfSelectedCountry"
                          :value="state.id"
                          :selected="stateShouldBeSelected(state)"
                          v-bind:key="state.id"
                          >{{ state.name }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('address.state_id')" class="text-danger">
                      {{ getError("address.state_id") }}
                    </p>
                  </section>
                  <!-- END State -->
                </div>

                <div class="row">
                  <!-- BEGIN City -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedOperator.address.city"
                      id="operator-address-city"
                      :label="$label('common.city', 'City')"
                      :placeholder="$label('common.city')"
                      :has-error="hasError('address.city')"
                      :error="getError('address.city')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END City -->

                  <!-- BEGIN Post code -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOperator.address.post_code"
                      id="operator-address-post-code"
                      :label="$label('common.post_code_short', 'Post code')"
                      :placeholder="$label('common.post_code_short')"
                      :has-error="hasError('address.post_code')"
                      :error="getError('address.post_code')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Post code -->
                </div>

                <div class="row">
                  <!-- BEGIN Steet name -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedOperator.address.street_name"
                      id="operator-address-street-name"
                      :label="$label('common.street_name_short', 'Street name')"
                      :placeholder="$label('common.street_name_long')"
                      :has-error="hasError('address.street_name')"
                      :error="getError('address.street_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Steert name -->

                  <!-- BEGIN Steert number -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOperator.address.street_number"
                      id="operator-address-street-number"
                      :label="$label('common.street_number', 'Street number')"
                      :placeholder="$label('common.street_number')"
                      :has-error="hasError('address.street_number')"
                      :error="getError('address.street_number')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Street number -->
                </div>

                <div class="row">
                  <!-- BEGIN additional information -->
                  <section class="col col-12">
                    <label class="label">{{
                      $label("common.additional_address_info_short")
                    }}</label>
                    <label class="textarea">
                      <textarea
                        v-model="
                          selectedOperator.address.additional_information
                        "
                        id="operator-address-additional-information"
                        :class="{
                          'text-input-error': hasError(
                            'address.additional_information'
                          )
                        }"
                        :placeholder="
                          $label('common.additional_address_info_long')
                        "
                        :disabled="updating"
                        rows="5"
                      >
                      </textarea>
                    </label>
                    <p
                      v-if="hasError('address.additional_information')"
                      class="text-danger"
                    >
                      {{ getError("address.additional_information") }}
                    </p>
                  </section>
                  <!-- END Additional information -->
                </div>

                <fieldset-button
                  text="Save address"
                  icon="fa fa-lg fa-lg fa-save"
                ></fieldset-button>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 tooltip-wrapper">
          <h2>
            <i class="fa fa-fw fa-lg fa-user"></i>
            {{ $label("operator.contact_person", "Contact person") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div v-if="selectedOperator !== null" class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Last name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedOperator.contact_person.last_name"
                      id="operator-contact-person-last-name"
                      :label="$label('common.last_name', 'Last name')"
                      :placeholder="$label('common.last_name')"
                      :has-error="hasError('contact_person.last_name')"
                      :error="getError('contact_person.last_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Last name -->

                  <!-- BEGIN First name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedOperator.contact_person.first_name"
                      id="operator-contact-person-first-name"
                      :label="$label('common.first_name', 'First name')"
                      :placeholder="$label('common.first_name')"
                      :has-error="hasError('contact_person.first_name')"
                      :error="getError('contact_person.first_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END First name -->
                </div>

                <div class="row">
                  <!-- BEGIN Email address -->
                  <section class="col col-9">
                    <text-input
                      v-model="selectedOperator.contact_person.email"
                      id="operator-contact-person-email"
                      :label="
                        $label('common.email_address_long', 'E-Mail addres')
                      "
                      :placeholder="$label('common.email_address_short')"
                      :has-error="hasError('contact_person.email')"
                      :error="getError('contact_person.email')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Email address -->

                  <!-- BEGIN Gender -->
                  <section class="col col-3">
                    <div class="btn-group">
                      <label class="label">{{ $label("common.gender") }}</label>
                      <label
                        @click="setGender(2)"
                        class="btn btn-default v-form-button"
                        :class="{ active: isFemale, 'is-disabled': updating }"
                      >
                        <i class="fa fa-fw fa-lg fa-female"></i>
                      </label>
                      <label
                        @click="setGender(1)"
                        class="btn btn-default v-form-button"
                        :class="{ active: isMale, 'is-disabled': updating }"
                      >
                        <i class="fa fa-fw fa-lg fa-male"></i>
                      </label>
                    </div>
                  </section>
                  <!-- END Gender -->
                </div>
                <div class="row">
                  <!-- BEGIN Phone country code -->
                  <section class="col col-3">
                    <text-input
                      v-model="
                        selectedOperator.contact_person.phone_number.prefix
                      "
                      id="operator-contact-person-phone-number-prefix"
                      :label="$label('common.prefix', 'Prefix')"
                      placeholder="+43"
                      :has-error="
                        hasError('contact_person.phone_number.prefix')
                      "
                      :error="getError('contact_person.phone_number.prefix')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Phone country code -->

                  <!-- BEGIN Phone number -->
                  <section class="col col-9">
                    <text-input
                      v-model="
                        selectedOperator.contact_person.phone_number.number
                      "
                      id="operator-contact-person-phone-number"
                      :label="$label('common.phone_number', 'Phone number')"
                      placeholder="XXXXXXXX"
                      :has-error="
                        hasError('contact_person.phone_number.number')
                      "
                      :error="getError('contact_person.phone_number.number')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateOperator"
                    />
                  </section>
                  <!-- END Phone number -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div class="row" style="margin-bottom: 16px;">
        <div class="col-xs-offset-4 col-xs-2">
          <button
            @click="updateOperator"
            :class="{ 'is-disabled': updating }"
            class="btn btn-lg btn-primary btn-block"
          >
            <span v-show="!updating"
              ><i class="fa fa-lg fa-lg fa-save"></i> save</span
            >
            <img
              v-show="updating"
              height="22px"
              :src="require('@/assets/img/loader.svg')"
            />
          </button>
        </div>
        <div class="col-xs-4">
          <a
            id="delete-operator-button"
            :class="{
              'is-disabled':
                updating || !selectedOperator || selectedOperator.is_default
            }"
            class="btn btn-lg btn-danger"
            @click="deleteOperator"
          >
            <i class="fa fa-lg fa-fw fa-trash-o"></i> Delete operator
          </a>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">&nbsp;</div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors.js";
import swal from "sweetalert";

import ViewLoader from "@/components/Common/ViewLoader.vue";
import NavigationTabs from "@/components/Settings/Operator/NavigationTabs.vue";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import FieldsetButton from "@/components/Common/Buttons/FieldsetButton.vue";
import Error404 from "@/components/Common/Error404";

export default {
  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    TextInput,
    FieldsetButton,
    Error404
  },

  data() {
    return {
      operators: [],
      loading: false,
      updating: false,
      noDataExist: false,
      selectedOperator: null,
      selectedOperatorId: null,
      countries: [],
      validatedInputs: [
        "name",
        "email",
        "website",
        "fiscal_details.uid",
        "fiscal_details.fn",
        "fiscal_details.zvr",
        "fiscal_details.dvr",
        "phone_number.prefix",
        "phone_number.number",
        "fax_number.prefix",
        "fax_number.number",
        "contact_person.first_name",
        "contact_person.last_name",
        "contact_person.email",
        "contact_person.phone_number.last_name",
        "contact_person.phone_number.prefix",
        "contact_person.phone_number.number",
        "address.country_id",
        "address.state_id",
        "address.city",
        "address.post_code",
        "address.street_name",
        "address.street_number",
        "address.additional_information"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedOperatorId = vm.$route.params.id;
      vm.getOperators(() => {
        vm.getOperator(() => {
          vm.getCountries();
        });
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getOperators(() => {
      this.getOperator(() => {
        this.getCountries();
      });
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    setGender(gender) {
      this.selectedOperator.contact_person.gender = gender;
    },

    createOperator() {
      const endpoint = this.$partnerEndpoint("operators");

      Api()
        .post(endpoint)
        .then(response => {
          this.selectedOperatorId = response.data.data.id;
        })
        .catch(error => {
          console.log(error);
        });
    },

    getOperators(successCallback) {
      const endpoint = this.$partnerEndpoint("operators");

      Api()
        .get(endpoint, {
          params: {
            page: 1,
            search: ""
          }
        })
        .then(response => {
          this.operators = response.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getOperator(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "operators/" + this.selectedOperatorId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedOperator = response.data.data;

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateOperator() {
      this.resetErrors();
      const endpoint = this.$partnerEndpoint(
        "operators/" + this.selectedOperatorId
      );

      Api()
        .put(endpoint, this.selectedOperator)
        .then(() => {
          this.getOperators(() => {
            this.getOperator(() => {
              swal({
                title: "Success!",
                text: "The operator was updated.",
                icon: "success"
              });
            });
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#operator-email", 500);
        });
    },

    deleteOperator() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The operator will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "operators/" + this.selectedOperatorId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "OperatorGeneralSettings",
                params: { id: "default" }
              });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete operators.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getCountries(successCallback) {
      Api()
        .get("api/internal/v1/countries")
        .then(response => {
          this.countries = response.data.data;

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    countryShouldBeSelected(country) {
      return this.selectedOperator.address.country_id == country.id;
    },

    stateShouldBeSelected(state) {
      return this.selectedOperator.address.state_id == state.id;
    }
  },

  computed: {
    isFemale() {
      return this.selectedOperator.contact_person.gender == 2;
    },

    isMale() {
      return this.selectedOperator.contact_person.gender == 1;
    },

    operatorName() {
      if (typeof this.operators.data == "undefined") {
        return "";
      }

      if (this.selectedOperatorId === "default" && this.selectedOperator) {
        return this.selectedOperator.name;
      }

      for (var i = 0; i < this.operators.data.length; i++) {
        if (this.operators.data[i].id == this.selectedOperatorId) {
          return this.operators.data[i].name;
        }
      }

      return "";
    },

    statesOfSelectedCountry() {
      for (var i = 0; i < this.countries.length; i++) {
        if (this.countries[i].id != this.selectedOperator.address.country_id) {
          continue;
        }
        return this.countries[i].states;
      }

      return [];
    }
  },

  watch: {
    selectedOperatorId(selectedOperatorId) {
      this.$router.push({
        name: "OperatorGeneralSettings",
        params: { id: selectedOperatorId }
      });
    }
  }
};
</script>
